import Arrow from "@assets/icons/back.svg";
import Calendar from "@assets/icons/calendar.svg";
import FinishFlag from "@assets/icons/finish-flag.svg";
import { useI18n } from "@core/i18n/i18n";
import { ProgramContent } from "@domain/program/program";
import { ProgramStatus } from "@domain/program/program-status/program-status";
import styled from "@emotion/styled";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { physioMeMobileBreakpoint, physioMeTabletBreakpoint, row } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNil } from "@utils/assert";
import React from "react";

interface ProgramDatesHeaderProps {
	programContent: ProgramContent;
	programStatus: ProgramStatus;
	onClick?: () => void;
}

const SEPARATOR = "-";

export const ProgramDatesHeader: React.FC<ProgramDatesHeaderProps> = (props: ProgramDatesHeaderProps) => {
	const { programStatus, programContent, onClick } = props;
	const { formatDateMed, formatDateShort } = useI18n();
	const { isDesktop } = useDimensions();

	const isEnded = programStatus === ProgramStatus.Ended;
	const textColor = isEnded ? colors.grey80 : colors.grey;

	if (isNil(programContent.startDate) || isNil(programContent.endDate)) {
		return null;
	}

	return (
		<DateContainer disabled={isEnded} onClick={!isEnded ? onClick : undefined}>
			{isEnded ? (
				<StyledFinishFlag width={24} height={24} color={colors.grey80} />
			) : (
				<Calendar width={24} height={24} color={colors.grey} />
			)}
			<span className="p-size-s-bold" style={{ color: textColor }}>
				{isDesktop ? formatDateMed(programContent.startDate) : formatDateShort(programContent.startDate)}
			</span>
			{!isDesktop ? (
				<span className="p-size-s-bold" style={{ color: textColor }}>
					{SEPARATOR}
				</span>
			) : (
				<StyledArrow width={24} height={24} color={textColor} />
			)}
			<span className="p-size-s-bold" style={{ color: textColor }}>
				{isDesktop ? formatDateMed(programContent.endDate) : formatDateShort(programContent.endDate)}
			</span>
		</DateContainer>
	);
};

const StyledFinishFlag = styled(FinishFlag)`
	transform: scaleX(-1);
`;

const StyledArrow = styled(Arrow)`
	transform: rotate(180deg);
`;

const DateContainer = styled.div<{ disabled: boolean }>`
	${row(8, "center", "center")};
	max-width: max-content;
	padding: 16px 32px;
	border-radius: 32px;
	height: 56px;
	background-color: ${({ disabled }) => (disabled ? colors.grey10 : colors.primary10)};
	cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
	border: 1px solid ${colors.transparent};

	:hover {
		border: 1px solid ${({ disabled }) => (disabled ? colors.grey10 : colors.primary25)};
	}

	:focus {
		border: 1px solid ${({ disabled }) => (disabled ? colors.grey10 : colors.primary)};
	}

	@media ${physioMeTabletBreakpoint} {
		padding: 8px 24px;
		border-radius: 24px;
	}

	@media ${physioMeMobileBreakpoint} {
		padding: 8px 16px;
	}
`;
